'use client';

import { Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cdnImageUrl } from '@kit/shared/storage';
import { Skeleton } from '@kit/ui/skeleton';

import type { ThumbnailSizeKey } from '../constants/thumbnail-sizes';
import { useDocumentUrl } from '../hooks/use-document-url';
import type { Document } from '../types';
import { FileTypeIcon } from './file-type-icon';

interface DocumentThumbnailProps {
  document: Document;
  size?: ThumbnailSizeKey;
  onClick?: () => void;
  className?: string;
  showOverlay?: boolean;
}

export function DocumentThumbnail({
  document,
  size = 'MEDIUM',
  onClick,
  className = '',
  showOverlay = true,
}: DocumentThumbnailProps) {
  const { t } = useTranslation('documents');
  const {
    thumbnailUrl,
    url,
    isThumbnailLoading,
    isLoading,
    supportsThumbnail,
    isPdf,
    isImage,
  } = useDocumentUrl({
    document,
    thumbnailSize: size,
  });

  // Determine loading state based on file type
  const loading = supportsThumbnail && (isThumbnailLoading || isLoading);

  // For images, prefer thumbnail URL, fallback to full URL
  // For PDFs, use the PDF thumbnail URL from Edge Function
  const displayUrl = thumbnailUrl || (isImage ? url : null);

  // Check if we have a displayable thumbnail
  const hasThumbnail = supportsThumbnail && displayUrl;

  return (
    // eslint-disable-next-line kit-components/use-kit-primitives -- full-bleed aspect-square thumbnail with absolute-positioned img/overlay children; kit Button's default h-9 height conflicts with aspect-square/w-full and would collapse the tile to 36px, with no size variant that removes the fixed height
    <button
      onClick={onClick}
      className={`group bg-muted/20 hover:border-primary relative aspect-square w-full overflow-hidden rounded-lg border-2 border-transparent transition-all ${className}`}
      type="button"
      aria-label={t('thumbnail.viewDocument', { fileName: document.fileName })}
    >
      {loading && <Skeleton className="absolute inset-0" />}

      {/* Image thumbnails - plain img on rotating signed/thumbnail URLs */}
      {!loading && isImage && displayUrl && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={cdnImageUrl(displayUrl, 'THUMBNAIL') ?? displayUrl}
          alt={document.fileName}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform group-hover:scale-105"
        />
      )}

      {/* PDF thumbnails - use img tag for SVG from Edge Function */}
      {!loading && isPdf && displayUrl && (
        // Using regular img tag because the Edge Function returns SVG
        // and Next.js Image has issues with external SVG sources
        // eslint-disable-next-line @next/next/no-img-element, ballee-media/no-untransformed-media -- SVG rendered by an Edge Function on a signed URL, not a public R2 raster asset; cdn-cgi/image can't transform it.
        <img
          src={displayUrl}
          alt={document.fileName}
          className="h-full w-full object-contain transition-transform group-hover:scale-105"
        />
      )}

      {/* Fallback to file type icon if no thumbnail available */}
      {!loading && !hasThumbnail && (
        <div className="bg-muted/30 absolute inset-0 flex items-center justify-center">
          <FileTypeIcon mimeType={document.mimeType} />
        </div>
      )}

      {showOverlay && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/40">
          <Eye className="h-6 w-6 text-white opacity-0 transition-opacity group-hover:opacity-100" />
        </div>
      )}
    </button>
  );
}
